import { Plane, Hotel, Target, CloudSun, Compass, Map, type LucideIcon } from 'lucide-react';
import { AGENT_EVENT_TYPES } from '../../constants';
import type { AgentEvent } from '../../types';

export interface ToolConfig {
  key: string;
  label: string;
  displayLabel: string; // shown in event log, e.g. "USING FLIGHTS TOOL"
  Icon: LucideIcon;
}

export const AGENT_TOOL_CONFIG: ToolConfig[] = [
  { key: 'planner',          label: 'Planner',    displayLabel: 'PLANNER',                 Icon: Map      },
  { key: 'research_travel',  label: 'Research',   displayLabel: 'USING RESEARCH AGENT',    Icon: Compass  },
  { key: 'flight_search',    label: 'Flights',    displayLabel: 'USING FLIGHTS TOOL',      Icon: Plane    },
  { key: 'hotel_search',     label: 'Hotels',     displayLabel: 'USING HOTELS TOOL',       Icon: Hotel    },
  { key: 'activities_finder',label: 'Activities', displayLabel: 'USING ACTIVITIES TOOL',   Icon: Target   },
  { key: 'weather_lookup',   label: 'Weather',    displayLabel: 'USING WEATHER TOOL',      Icon: CloudSun },
];

// Keys that appear in the status bar (exclude the orchestrator agent)
export const STATUS_BAR_TOOLS = AGENT_TOOL_CONFIG.filter(t => t.key !== 'research_travel');

export function resolveToolConfig(toolName?: string, eventType?: string): ToolConfig | undefined {
  if (eventType === AGENT_EVENT_TYPES.AGENT_START || eventType === AGENT_EVENT_TYPES.AGENT_COMPLETE) {
    return AGENT_TOOL_CONFIG.find(t => t.key === 'planner');
  }
  if (!toolName) return undefined;
  const lower = toolName.toLowerCase();
  return AGENT_TOOL_CONFIG.find(t => lower.includes(t.key) || t.key.includes(lower));
}

export type ToolState = 'active' | 'completed' | 'idle';


export function getToolStates(events: AgentEvent[]): Record<string, ToolState> {
  const activeTools = new Set<string>();
  const completedTools = new Set<string>();

  for (const evt of events) {
    if (evt.eventType === AGENT_EVENT_TYPES.AGENT_START) {
      activeTools.add('planner');
    }
    if (evt.eventType === AGENT_EVENT_TYPES.AGENT_COMPLETE) {
      activeTools.delete('planner');
      completedTools.add('planner');
    }
    if (evt.eventType === AGENT_EVENT_TYPES.TOOL_START && evt.toolName) {
      const key = resolveToolConfig(evt.toolName)?.key ?? evt.toolName.toLowerCase();
      activeTools.add(key);
    }
    if (evt.eventType === AGENT_EVENT_TYPES.TOOL_END && evt.toolName) {
      const key = resolveToolConfig(evt.toolName)?.key ?? evt.toolName.toLowerCase();
      activeTools.delete(key);
      completedTools.add(key);
    }
  }

  const states: Record<string, ToolState> = {};
  for (const { key } of STATUS_BAR_TOOLS) {
    if (completedTools.has(key)) states[key] = 'completed';
    else if (activeTools.has(key)) states[key] = 'active';
    else states[key] = 'idle';
  }
  return states;
}
